import React, {useState} from 'react';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import AsyncSelect from 'react-select/async';
import {MultiValue} from 'react-select';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {buildNotificationURL} from '@/utils/urlBuilder';
import {useFormik} from 'formik';
import * as yup from 'yup';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import {useStoreActions} from 'easy-peasy';
import i18n from 'i18n';

interface User {
    id: number;
    identifier: string;
    email: string;
}

interface UserOption {
    label: string;
    value: number | string;
}

interface FormValues {
    users: UserOption[];
}

interface NotificationConfigureFormProps {
    notificationId: number;
    onFinish: () => void;
}

const loadStoryUsersRequest = (inputValue: string) => axios.get<User[]>(window.URLS.userSearch, {
    params: {
        query: inputValue,
    },
})
    .catch((error) => {
        if (error.response.status === 400) {
            return error.response;
        }

        throw error;
    });
const loadStoryUsersRequestDebounced = AwesomeDebouncePromise(loadStoryUsersRequest, 280);

const NotificationConfigureFormSchema = yup.object({
    users: yup.array().of(
        yup.object({
            value: yup.number().required(),
            label: yup.string().required(),
        }).required(),
    ).required(i18n.t('jt_notification_select_user_required.text')),
});

const NotificationConfigureForm = React.memo<NotificationConfigureFormProps>(({
    notificationId,
    onFinish,
}) => {
    const editNotificationConfigure = useStoreActions((actions: any) => actions.notifications.editNotificationConfigure);

    const [userOptions, setUserOptions] = useState<UserOption[]>([]);

    const onSubmit = (values: FormValues) => {
        axios.post(buildNotificationURL('configureNotification', notificationId), values)
            .then((response) => {
                formik.setSubmitting(false);
                editNotificationConfigure([notificationId, response.data.usersConfigured]);
                onFinish();
            })
            .catch(console.error);
    };

    const formik = useFormik<FormValues>({
        validationSchema: NotificationConfigureFormSchema,
        initialValues: {
            users: [],
        },
        onSubmit,
    });

    const getUserOptions = async (inputValue: string): Promise<UserOption[]> => {
        const results = await loadStoryUsersRequestDebounced(inputValue);

        const selectAllOption: UserOption = {
            label: i18n.t('jt_st_select_all.text'),
            value: '*',
        };

        const userChoices: UserOption[] = results.data.map((user: User) => ({
            label: `${user.identifier} (${user.email})`,
            value: user.id,
        }));
        setUserOptions(userChoices);

        return [
            ...(inputValue || !userChoices.length ? [] : [selectAllOption]),
            ...userChoices,
        ];
    };

    const onUserSelection = (value: MultiValue<UserOption>) => {
        if (value && value.length && value[value.length - 1].value === '*') {
            formik.setFieldValue('users', userOptions);
        } else {
            formik.setFieldValue('users', value || []);
        }
    };

    return (
        <Form noValidate onSubmit={formik.handleSubmit}>
            <div className='form-row'>
                <Form.Group className="mb-3" controlId="users" as={Col} xs="10" >
                    <AsyncSelect
                        isMulti
                        cacheOptions
                        defaultOptions
                        loadOptions={getUserOptions}
                        hideSelectedOptions={false}
                        onChange={onUserSelection}
                        value={formik.values.users}
                        styles={{
                            valueContainer: (provided) => ({
                                ...provided,
                                maxHeight: '100px',
                                overflowY: 'scroll',
                                scrollbarWidth: 'none',
                                overflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    width: 0,
                                    height: 0,
                                },
                            }),
                        }}
                    />
                    <Form.Control.Feedback
                        type="invalid"
                        style={{display: formik.errors.users ? 'block' : 'none'}}
                    >
                        {typeof formik.errors.users === 'string' ? formik.errors.users : ''}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="notificationTitle-save" as={Col} xs="2" >
                    <Button
                        variant="info"
                        fullWidth
                        type="submit"
                        disabled={formik.isSubmitting}
                        data-cy="notification-configure-info-btn">
                        {
                            formik.isSubmitting ? (
                                <Spinner
                                    variant="border"
                                    size="sm"
                                />
                            ) : 'Add'
                        }
                    </Button>
                </Form.Group>
            </div >
        </Form >
    );
});

NotificationConfigureForm.displayName = 'NotificationConfigureForm';

export default NotificationConfigureForm; 